// A tissue pack's cell in the inspector (spec §15's Neuron fields, from the pack's own tables): what kind
// of cell it is and its type, its arbor's cable, its synapses where the pack holds all of them, and its
// firing rate in the run the viewport plays. The columns the catalogue doesn't read load on the first
// cell selected, and a run's rates on the first cell selected while it plays; both are cached by pack.

import type { PackInfo } from './catalogue.ts';
import type { ActivityIndex } from './activity.ts';
import { HAS_MESH, type TissueTables } from './tissue.ts';

/** `neurons/cut.u8`: bit 0, the skeleton reaches a slab face; bit 1, it leaves the annotated tissue. */
const CUT = 3;
/** Superclasses a tissue pack gives its other cells (H01's glia, vascular and unclassified cells): no run models them. */
const NOT_NEURONS = new Set(['glia', 'vascular', 'unknown']);
const SPECIES: Record<string, string> = { 'Homo sapiens': 'Human', 'Mus musculus': 'Mouse', 'Drosophila melanogaster': 'Fly' };

/** The eyebrow's word for a pack's species: "Human" for `Homo sapiens`. */
export const speciesLabel = (species: string | undefined): string | null => (species ? (SPECIES[species] ?? species) : null);

/** The neuron table's columns the inspector reads past the catalogue's; null where the pack has none. */
export interface CellMeasures {
  /** Cable of the source skeleton, mm; NaN without one. */
  cableMm: Float32Array | null;
  cut: Uint8Array | null;
  /** Rows of the pack's synapse table with the cell presynaptic (out) and postsynaptic (in). */
  synPre: Uint32Array | null;
  synPost: Uint32Array | null;
}

const measures = new Map<string, Promise<CellMeasures>>();

/** A pack's cable, cut and synapse columns, fetched once a pack. A column whose length isn't the table's reads as missing. */
export function loadCellMeasures(t: TissueTables, fetchFn: typeof fetch = fetch): Promise<CellMeasures> {
  let p = measures.get(t.base);
  if (!p) {
    const n = t.table.bodyId.length;
    const column = async <A>(name: string, bytes: number, make: (b: ArrayBuffer) => A): Promise<A | null> => {
      const f = t.files[name]?.file;
      const res = f ? await fetchFn(`${t.base}neurons/${f}`).catch(() => null) : null;
      if (!res?.ok) return null;
      const buf = await res.arrayBuffer();
      return buf.byteLength === n * bytes ? make(buf) : null;
    };
    p = Promise.all([
      column('cable_mm', 4, (b) => new Float32Array(b)),
      column('cut', 1, (b) => new Uint8Array(b)),
      column('syn_pre', 4, (b) => new Uint32Array(b)),
      column('syn_post', 4, (b) => new Uint32Array(b)),
    ]).then(([cableMm, cut, synPre, synPost]) => ({ cableMm, cut, synPre, synPost }));
    measures.set(t.base, p);
    p.catch(() => measures.delete(t.base));
  }
  return p;
}

const rows = new WeakMap<Uint32Array, Map<number, number>>();

/** A body id's row in the neuron table, which is also its index in the pack's runs; null if it isn't there. */
export function rowOf(t: TissueTables, body: number): number | null {
  let m = rows.get(t.table.bodyId);
  if (!m) {
    m = new Map();
    for (let i = 0; i < t.table.bodyId.length; i++) m.set(t.table.bodyId[i]!, i);
    rows.set(t.table.bodyId, m);
  }
  return m.get(body) ?? null;
}

/** The layer as the Type field says it: L2 → "layer 2", WM → "white matter". */
const layerWords = (layer: string) => (layer === 'WM' ? 'white matter' : layer.replace(/^L(?=\d)/, 'layer '));

/** Spec §15's Type: the cell type, then its layer ("Pyramidal, layer 2"), as design writes it. */
export function typeLine(type: string, layer: string | undefined): string {
  const noun = type.replace(/^./, (c) => c.toUpperCase());
  return layer && layer !== 'unknown' ? `${noun}, ${layerWords(layer)}` : noun;
}

export interface CellFacts {
  /** Its superclass's name, for the eyebrow: "Neuron", "Glia". */
  kind: string | null;
  /** A neuron, so the pack's runs may model it. */
  neuron: boolean;
  type: string | null;
  /** Its traced arbor's cable, mm, and whether the arbor runs out of the imaged tissue. */
  arbor: { mm: number; cut: boolean } | null;
  /** Its synapses in and out, where the pack holds every one of them (a proofread cell's). */
  synapses: { in: number; out: number } | null;
  /** An arbor was traced (by hand or automatically), not its soma alone. */
  traced: boolean;
}

/** A cell's fields from the tables, at its row; `m` null until the extra columns load. */
export function cellFacts(t: TissueTables, pack: PackInfo, m: CellMeasures | null, row: number): CellFacts {
  const ty = t.celltypes.types.find((x) => row >= x.first && row < x.first + x.count);
  const sup = ty ? t.celltypes.superclasses.find((c) => c.id === ty.superclass) : undefined;
  const layer = t.table.layer ? pack.enums?.layer?.[t.table.layer[row]!] : undefined;
  const proofread = (t.table.flags[row]! & HAS_MESH) !== 0;
  const cable = m?.cableMm?.[row];
  return {
    kind: sup?.name ?? null,
    neuron: !!ty && !NOT_NEURONS.has(ty.superclass),
    type: ty ? typeLine(ty.name, layer) : null,
    arbor: cable !== undefined && Number.isFinite(cable) && cable > 0 ? { mm: cable, cut: ((m?.cut?.[row] ?? 0) & CUT) !== 0 } : null,
    synapses: proofread && m?.synPre && m.synPost ? { in: m.synPost[row]!, out: m.synPre[row]! } : null,
    traced: (t.table.skeleton?.[row] ?? (proofread ? 1 : 0)) > 0,
  };
}

/** The Data line for a tissue pack's cell: what the run plays on it, else how it was reconstructed. */
export function cellData(f: CellFacts, pack: PackInfo, simulated: boolean): string {
  if (simulated && f.neuron) return 'Simulated on its traced wiring';
  const whose = pack.species === 'Homo sapiens' ? 'one person’s' : 'one specimen’s';
  return `${f.traced ? 'Traced' : 'Segmented'} from ${whose} electron micrographs`;
}

/** A run's `rates.u8` (connectome-pack-v0, optional): one byte per neuron and rate frame, row-major by frame. */
export interface RunRates {
  id: string;
  frames: number;
  frameMs: number;
  scaleHz: number;
  /** Neurons per frame: the pack's neuron table. */
  n: number;
  data: Uint8Array;
}

const rates = new Map<string, Promise<RunRates | null>>();

/** A run's rates, checked against its run.json and the pack's `n` neurons; null where the run has none. */
export function loadRunRates(base: string, index: ActivityIndex, id: string, n: number, fetchFn: typeof fetch = fetch): Promise<RunRates | null> {
  const key = `${base}|${id}`;
  let p = rates.get(key);
  if (!p) {
    p = (async () => {
      const entry = index.runs.find((r) => r.id === id);
      if (!entry) return null;
      const res = await fetchFn(base + entry.path);
      if (!res.ok) throw new Error(`${base}${entry.path}: HTTP ${res.status}`);
      const r = ((await res.json()) as { rates?: { file?: unknown; frames?: unknown; frame_ms?: unknown; scale_hz?: unknown } }).rates;
      const frames = typeof r?.frames === 'number' && r.frames > 0 ? r.frames : 0;
      const frameMs = typeof r?.frame_ms === 'number' && r.frame_ms > 0 ? r.frame_ms : 0;
      if (typeof r?.file !== 'string' || !r.file || !frames || !frameMs) return null;
      const url = base + entry.path.replace(/[^/]*$/, '') + r.file;
      const got = await fetchFn(url);
      if (!got.ok) throw new Error(`${url}: HTTP ${got.status}`);
      const data = new Uint8Array(await got.arrayBuffer());
      if (data.length !== frames * n) throw new Error(`${url}: ${data.length} bytes, expected ${frames} frames × ${n} neurons`);
      return { id, frames, frameMs, scaleHz: typeof r.scale_hz === 'number' && r.scale_hz > 0 ? r.scale_hz : 1, n, data };
    })();
    rates.set(key, p);
    p.catch(() => rates.delete(key));
  }
  return p;
}

/** One neuron's rate in each rate frame, Hz, and its mean over the run. */
export function rateOf(r: RunRates, row: number): { hz: Float32Array; meanHz: number } {
  const hz = new Float32Array(r.frames);
  let sum = 0;
  for (let j = 0; j < r.frames; j++) {
    const v = r.data[j * r.n + row]! * r.scaleHz;
    hz[j] = v;
    sum += v;
  }
  return { hz, meanHz: sum / r.frames };
}
